// Annual refresh: this config lists named season instances by specific year.
// At launch (April 2026) the 10 entries below cover the rolling 12-month window.
// Add next-year instances (e.g. "Alps Winter 2027/28", "Med Summer 2027") before
// the current set drops off the rolling window (early 2027 onwards).

export type SeasonCategory = "winter" | "summer" | "festival";
export type SeasonHemisphere = "northern" | "southern" | "tropical";

export type Season = {
  slug: string;
  name: string;
  shortName: string;
  typicalStart: string;
  typicalEnd: string;
  category: SeasonCategory;
  hemisphere: SeasonHemisphere;
  region: string;
  blurb: string;
  workTypes: string[];
  detailUrl: string;
};

export const SEASONS: Season[] = [
  {
    slug: "alps-winter-2026-27",
    name: "Alps Winter 2026/27",
    shortName: "Alps Winter",
    typicalStart: "2026-11-28",
    typicalEnd: "2027-04-24",
    category: "winter",
    hemisphere: "northern",
    region: "Europe",
    blurb:
      "The biggest winter season in the world. French, Swiss, Austrian and Italian resorts hire chalet hosts, ski instructors, bar staff and drivers from late November to Easter.",
    workTypes: [
      "Chalet host",
      "Ski / snowboard instructor",
      "Bar staff",
      "Transfer driver",
      "Kitchen",
    ],
    detailUrl: "/jobs?season=snow",
  },
  {
    slug: "north-america-winter-2026-27",
    name: "North America Winter 2026/27",
    shortName: "North America Winter",
    typicalStart: "2026-11-21",
    typicalEnd: "2027-04-18",
    category: "winter",
    hemisphere: "northern",
    region: "North America",
    blurb:
      "Resorts in Canada and the western US run late November to mid April. J1 visa common for European workers. Whistler, Banff, Jackson Hole, Park City.",
    workTypes: ["Lift ops", "Ski / snowboard instructor", "Hospitality", "Rental tech"],
    detailUrl: "/jobs?season=snow",
  },
  {
    slug: "japan-winter-2026-27",
    name: "Japan Winter 2026/27",
    shortName: "Japan Winter",
    typicalStart: "2026-12-05",
    typicalEnd: "2027-03-28",
    category: "winter",
    hemisphere: "northern",
    region: "Asia",
    blurb:
      "Niseko, Hakuba and Furano open early December through late March. The deepest powder season on the calendar. English-speaking roles in hotels, rentals and schools.",
    workTypes: ["Ski / snowboard instructor", "Hotel staff", "Rental tech"],
    detailUrl: "/jobs?season=snow",
  },
  {
    slug: "south-america-ski-2026",
    name: "South America Ski 2026",
    shortName: "South America Ski",
    typicalStart: "2026-06-13",
    typicalEnd: "2026-10-04",
    category: "winter",
    hemisphere: "southern",
    region: "South America",
    blurb:
      "Chile and Argentina flip the calendar. Portillo, Valle Nevado and Las Leñas run mid June to early October. Popular with instructors chasing back-to-back winters.",
    workTypes: ["Ski / snowboard instructor", "Hospitality"],
    detailUrl: "/jobs?season=snow",
  },
  {
    slug: "med-summer-2026",
    name: "Med Summer 2026",
    shortName: "Med Summer",
    typicalStart: "2026-04-25",
    typicalEnd: "2026-10-25",
    category: "summer",
    hemisphere: "northern",
    region: "Europe",
    blurb:
      "The superyacht season across the Med and Greek Islands. Antibes, Palma, Mykonos, Santorini. Yacht crew, hospitality, watersports and villa work from late April through October.",
    workTypes: ["Yacht crew", "Stewardess", "Chef", "Deckhand", "Villa host", "Bar staff", "Watersports instructor"],
    detailUrl: "/jobs?season=sun",
  },
  {
    slug: "caribbean-winter-2026-27",
    name: "Caribbean Winter 2026/27",
    shortName: "Caribbean",
    typicalStart: "2026-11-07",
    typicalEnd: "2027-04-25",
    category: "summer",
    hemisphere: "tropical",
    region: "Caribbean",
    blurb:
      "Charter season: November to April. Yachts migrate from the Med to the Caribbean for the northern winter. St Barts, Antigua, the BVI. Warm-weather work when Europe freezes.",
    workTypes: ["Yacht crew", "Stewardess", "Deckhand", "Watersports instructor"],
    detailUrl: "/jobs?season=sun",
  },
  {
    slug: "scandinavia-summer-2026",
    name: "Scandinavia Summer 2026",
    shortName: "Scandinavia Summer",
    typicalStart: "2026-06-06",
    typicalEnd: "2026-08-29",
    category: "summer",
    hemisphere: "northern",
    region: "Europe",
    blurb:
      "Short intense summer across Norway, Sweden and Finland. Adventure tourism, fjord cruising and wilderness guiding. Midnight sun from June through August.",
    workTypes: ["Adventure guide", "Hospitality", "Yacht crew"],
    detailUrl: "/jobs?season=sun",
  },
  {
    slug: "australia-summer-2026-27",
    name: "Australia Summer 2026/27",
    shortName: "Australia Summer",
    typicalStart: "2026-12-05",
    typicalEnd: "2027-02-27",
    category: "summer",
    hemisphere: "southern",
    region: "Australia",
    blurb:
      "Australian summer peaks December to February. Working holiday visa common. Whitsundays sailing, Gold Coast hospitality, Margaret River vineyard work.",
    workTypes: ["Yacht crew", "Hospitality", "Watersports instructor"],
    detailUrl: "/jobs?season=sun",
  },
  {
    slug: "uk-festival-season-2026",
    name: "UK Festival Season 2026",
    shortName: "UK Festivals",
    typicalStart: "2026-06-06",
    typicalEnd: "2026-09-26",
    category: "festival",
    hemisphere: "northern",
    region: "UK",
    blurb:
      "Glastonbury kicks off late June. Then Latitude, Boomtown, Reading, Leeds, Creamfields. Bar, catering, stewarding and production roles run through to the end of September.",
    workTypes: ["Bar staff", "Catering", "Steward", "Production crew"],
    detailUrl: "/jobs?season=festival",
  },
];
